<template>
  <template v-for="(block, index) in blocks" :key="index">
    <LazyAlumniDropOut
      v-if="
        block.__typename === 'InternalFormBlock' &&
        (block.form?.slug?.cs.startsWith('alumni-drop-out') ||
          block.form?.slug?.en.startsWith('alumni-drop-out'))
      "
    />

    <LazyAlumniForm
      v-else-if="
        block.__typename === 'InternalFormBlock' &&
        (block.form?.slug?.cs.startsWith('alumni') ||
          block.form?.slug?.en.startsWith('alumni'))
      "
    />

    <component
      v-else-if="resolveComponent(block)"
      :is="resolveComponent(block)"
      v-bind="resolveProps(block, index)"
    />
  </template>
</template>

<script setup lang="ts">
import type { Component } from 'vue'

import {
  LazyPersonsSection,
  LazyAccordion,
  LazyWysiwyg,
  LazyRelatedPages,
  LazyEmbedBlock,
  LazyCascadingImages,
  LazyAlumniForm,
  LazyAlumniDropOut,
  LazyLinksSection,
} from '#components'
import type { ContentBlock, ImageModalType } from '~/types'

interface Props {
  blocks: ContentBlock[]
}

const props = defineProps<Props>()
const { locale } = useI18n()

const componentMap: Record<string, Component> = {
  ContactsBlock: LazyPersonsSection,
  TextBlock: LazyWysiwyg,
  AccordionBlock: LazyAccordion,
  EmbedBlock: LazyEmbedBlock,
  GalleryBlock: LazyCascadingImages,
}

const resolveComponent = (block: ContentBlock) => {
  if (block.__typename === 'LinksBlock') {
    return block.display === 'buttons' ? LazyRelatedPages : LazyLinksSection
  }
  return componentMap[block.__typename]
}

const resolveProps = (block: ContentBlock, index: number) => {
  if (block.__typename === 'AccordionBlock') {
    return {
      heading: block.title,
      items: block.items,
      background: block.background,
    }
  }

  if (block.__typename === 'ContactsBlock') {
    return {
      title: block.title[locale.value],
      people: block.people,
      background: block.background,
    }
  }

  if (block.__typename === 'TextBlock') {
    const nextBlock = props.blocks[index + 1]
    const hasNextWysiwyg = nextBlock?.__typename === 'TextBlock'
    return {
      heading: block.heading,
      content: block.content_nodes[locale.value],
      background: block.background,
      buttonColor: 'light',
      hasNextWysiwyg,
    }
  }

  if (block.__typename === 'LinksBlock') {
    const title = block.title?.[locale.value] ?? ''

    if (block.display === 'buttons') {
      return {
        title,
        pages:
          block.links?.map((l) => ({
            title: l.label?.[locale.value] || l.page?.title || '',
            url: l.url || l.page?.slug,
            target: l.target,
            color: l.color,
            type: l.type,
          })) || [],
      }
    }

    return {
      title,
      links:
        block.links?.map((l) => ({
          type: l.type,
          color: l.color,
          title: l.label?.[locale.value] || l.page?.title || '',
          slug: l.page?.slug?.[locale.value] ?? null,
          url: l.url ?? null,
          target: l.target,
        })) || [],
    }
  }

  if (block.__typename === 'EmbedBlock') {
    return {
      code: block.code,
      background: block.background,
    }
  }

  if (block.__typename === 'GalleryBlock') {
    return {
      heading: block.heading,
      images: block.images.map((img: ImageModalType) => ({
        id: Number(img.id) || 0,
        url: img.url || img.media?.url || '',
        alt_text: img.alt_text || '',
        caption: img.caption || img.alt_text || '',
      })),
      background: block.background,
    }
  }

  return block
}
</script>
